import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiPlus, FiTrash2, FiSave, FiX, FiSearch, FiChevronDown, FiUserPlus, FiEdit2, FiCheck, FiAlertCircle, FiLoader } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { invoicesAPI, productsAPI, customersAPI, settingsAPI } from '../../services/api';
import AddCustomerModal from '../../components/common/AddCustomerModal';

const InvoiceForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  // Form state
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);

  // Customer dropdown state
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [customerToEdit, setCustomerToEdit] = useState(null);
  const customerDropdownRef = useRef(null);

  // Product search dropdown state (per item row)
  const [productSearches, setProductSearches] = useState({});
  const [showProductDropdown, setShowProductDropdown] = useState({});
  const productDropdownRefs = useRef({});

  // GST lookup state
  const [gstLookupStatus, setGstLookupStatus] = useState('idle'); // 'idle', 'loading', 'found', 'not_found', 'invalid'
  const [gstLookupTimeout, setGstLookupTimeout] = useState(null);

  // GST Type state (IGST vs CGST/SGST)
  const [companyState, setCompanyState] = useState('Delhi');
  const [gstType, setGstType] = useState('IGST'); // 'IGST' or 'CGST_SGST'

  // Tax disable option
  const [taxDisabled, setTaxDisabled] = useState(false);

  const [formData, setFormData] = useState({
    invoice_type: 'tax_invoice',
    invoice_date: new Date().toISOString().split('T')[0],
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    customer_gstin: '',
    payment_status: 'unpaid',
    payment_mode: '',
    delivery_status: 'pending',
    paid_amount: 0,
    notes: '',
    terms: 'Payment due within 30 days.\nThank you for your business!',
    supply_address: '',
    supply_city: '',
    supply_state: '',
    supply_pincode: ''
  });

  const [items, setItems] = useState([
    {
      product_id: '',
      item_name: '',
      description: '',
      hsn_sac: '',
      quantity: 1,
      unit: 'PCS',
      location: 'Office',
      rate: 0,
      tax_rate: 18,
      discount: 0,
      tax_amount: 0,
      amount: 0
    }
  ]);

  const [totals, setTotals] = useState({
    subtotal: 0,
    total_discount: 0,
    total_tax: 0,
    grand_total: 0,
    due_amount: 0
  });

  useEffect(() => {
    fetchProducts();
    fetchCustomers();
    fetchCompanySettings();
    if (isEditMode) {
      fetchInvoice();
    }
  }, [id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
        setShowCustomerDropdown(false);
      }
      // Close product dropdowns when clicking outside
      Object.keys(productDropdownRefs.current).forEach(idx => {
        const ref = productDropdownRefs.current[idx];
        if (ref && !ref.contains(event.target)) {
          setShowProductDropdown(prev => ({ ...prev, [idx]: false }));
        }
      });
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch company settings for GST type determination
  const fetchCompanySettings = async () => {
    try {
      const response = await settingsAPI.get();
      const settings = response.data.data;
      const state = settings?.company_state || 'Delhi';
      const cleanState = state.includes('-') ? state.split('-')[1].trim() : state.trim();
      setCompanyState(cleanState);
    } catch (error) {
      console.error('Failed to fetch company settings:', error);
      setCompanyState('Delhi');
    }
  };

  // Determine GST type based on customer state vs company state
  const determineGstType = (customerState) => {
    if (!customerState) return 'IGST';
    const normalizeState = (state) => {
      if (!state) return '';
      const cleaned = state.includes('-') ? state.split('-')[1].trim() : state.trim();
      return cleaned.toLowerCase();
    };

    const normalizedCustomerState = normalizeState(customerState);
    const normalizedCompanyState = normalizeState(companyState);

    if (normalizedCustomerState === normalizedCompanyState) {
      return 'CGST_SGST';
    }
    return 'IGST';
  };

  useEffect(() => {
    calculateTotals();
  }, [items, formData.paid_amount, taxDisabled]);

  // Recalculate GST type when Place of Supply state changes
  useEffect(() => {
    if (formData.supply_state) {
      const newGstType = determineGstType(formData.supply_state);
      setGstType(newGstType);
    } else if (selectedCustomer?.state) {
      const newGstType = determineGstType(selectedCustomer.state);
      setGstType(newGstType);
    }
  }, [formData.supply_state, selectedCustomer, companyState]);

  // GST Number validation regex
  const isValidGSTIN = (gstin) => {
    const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstinRegex.test(gstin);
  };

  // GST Lookup function - called on keyup/paste with debounce
  const lookupGSTIN = useCallback(async (gstin) => {
    if (!gstin || gstin.length < 15) {
      setGstLookupStatus('idle');
      return;
    }

    const cleanGstin = gstin.toUpperCase().trim();

    if (!isValidGSTIN(cleanGstin)) {
      setGstLookupStatus('invalid');
      return;
    }

    setGstLookupStatus('loading');

    try {
      const response = await customersAPI.lookupGstin(cleanGstin);
      if (response.data.status === 'success' && response.data.data) {
        const customer = response.data.data;

        if (response.data.exists) {
          // Existing customer found - auto-select them
          setSelectedCustomer({
            id: customer.id,
            name: customer.name || '',
            company_name: customer.company_name || '',
            email: customer.email || '',
            phone: customer.phone || '',
            gst_number: cleanGstin,
            address: customer.address || '',
            city: customer.city || '',
            state: customer.state || '',
            pincode: customer.pincode || ''
          });
          setCustomerSearch(customer.name + (customer.company_name ? ` (${customer.company_name})` : ''));

          // Auto-fill form data
          setFormData(prev => ({
            ...prev,
            customer_name: customer.name,
            customer_email: customer.email || '',
            customer_phone: customer.phone || '',
            customer_gstin: cleanGstin,
            customer_address: [customer.address, customer.city, customer.state, customer.pincode].filter(Boolean).join(', '),
            supply_address: prev.supply_address || customer.address || '',
            supply_city: prev.supply_city || customer.city || '',
            supply_state: prev.supply_state || customer.state || '',
            supply_pincode: prev.supply_pincode || customer.pincode || ''
          }));

          setGstLookupStatus('found');
          toast.success('Customer found! Details auto-filled.');
        } else {
          // New GST - auto-fill state from GST code
          setFormData(prev => ({
            ...prev,
            customer_gstin: cleanGstin,
            supply_state: prev.supply_state || customer.state || ''
          }));
          setGstLookupStatus('not_found');
        }
      } else {
        setGstLookupStatus('not_found');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setGstLookupStatus('not_found');
      } else {
        console.error('GST lookup error:', error);
        setGstLookupStatus('idle');
      }
    }
  }, []);

  // Handle GST input change with debounce
  const handleGstChange = (e) => {
    const value = e.target.value.toUpperCase();
    setFormData(prev => ({ ...prev, customer_gstin: value }));
    setGstLookupStatus('idle');

    // Clear existing timeout
    if (gstLookupTimeout) {
      clearTimeout(gstLookupTimeout);
    }

    // Set new timeout for lookup (debounce 500ms)
    if (value.length >= 15) {
      const timeout = setTimeout(() => {
        lookupGSTIN(value);
      }, 500);
      setGstLookupTimeout(timeout);
    }
  };

  // Handle GST paste - immediate lookup
  const handleGstPaste = (e) => {
    setTimeout(() => {
      const value = e.target.value.toUpperCase();
      if (value.length >= 15) {
        lookupGSTIN(value);
      }
    }, 100);
  };

  // GST status indicator
  const getGstStatusIndicator = () => {
    switch (gstLookupStatus) {
      case 'loading':
        return <FiLoader className="animate-spin text-blue-500" size={18} />;
      case 'found':
        return <FiCheck className="text-green-500" size={18} />;
      case 'not_found':
        return <span className="text-xs text-orange-600">New</span>;
      case 'invalid':
        return <FiAlertCircle className="text-red-500" size={18} />;
      default:
        return null;
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await productsAPI.getAll({ limit: 1000, status: 'active' });
      setProducts(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      setCustomersLoading(true);
      const response = await customersAPI.getAllForDropdown();
      setCustomers(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setCustomersLoading(false);
    }
  };

  // Handle customer selection
  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.name + (customer.company_name ? ` (${customer.company_name})` : ''));
    setShowCustomerDropdown(false);

    // Determine GST type based on customer state
    const newGstType = determineGstType(customer.state);
    setGstType(newGstType);

    // Auto-fill customer fields and Place of Supply with customer's address
    setFormData(prev => ({
      ...prev,
      customer_name: customer.name,
      customer_email: customer.email || '',
      customer_phone: customer.phone || '',
      customer_gstin: customer.gst_number || '',
      customer_address: [
        customer.address,
        customer.city,
        customer.state,
        customer.pincode
      ].filter(Boolean).join(', '),
      supply_address: prev.supply_address || customer.address || '',
      supply_city: prev.supply_city || customer.city || '',
      supply_state: prev.supply_state || customer.state || '',
      supply_pincode: prev.supply_pincode || customer.pincode || ''
    }));

    // Update GST lookup status
    if (customer.gst_number) {
      setGstLookupStatus('found');
    } else {
      setGstLookupStatus('idle');
    }
  };

  // Handle new customer saved from modal
  const handleCustomerSaved = (newCustomer) => {
    setCustomers(prev => [newCustomer, ...prev]);
    handleCustomerSelect(newCustomer);
    setShowAddCustomerModal(false);
  };

  // Handle customer updated from edit modal
  const handleCustomerUpdated = (updatedCustomer) => {
    setCustomers(prev =>
      prev.map(c => c.id === updatedCustomer.id ? updatedCustomer : c)
    );
    handleCustomerSelect(updatedCustomer);
    setShowEditCustomerModal(false);
    setCustomerToEdit(null);
  };

  // Handle edit customer button click
  const handleEditCustomer = () => {
    if (selectedCustomer) {
      setCustomerToEdit(selectedCustomer);
      setShowEditCustomerModal(true);
    }
  };

  // Filter customers based on search
  const filteredCustomers = customers.filter(customer => {
    const search = customerSearch.toLowerCase();
    return (
      customer.name?.toLowerCase().includes(search) ||
      customer.company_name?.toLowerCase().includes(search) ||
      customer.phone?.includes(search) ||
      customer.gst_number?.toLowerCase().includes(search)
    );
  });

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const response = await invoicesAPI.getById(id);
      const invoice = response.data.data;

      const formattedDate = invoice.invoice_date
        ? new Date(invoice.invoice_date).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0];

      setFormData({
        invoice_type: invoice.invoice_type,
        invoice_date: formattedDate,
        customer_name: invoice.customer_name || '',
        customer_email: invoice.customer_email || '',
        customer_phone: invoice.customer_phone || '',
        customer_address: invoice.customer_address || '',
        customer_gstin: invoice.customer_gstin || '',
        payment_status: invoice.payment_status,
        payment_mode: invoice.payment_mode || '',
        delivery_status: invoice.delivery_status || 'pending',
        paid_amount: parseFloat(invoice.paid_amount || 0),
        notes: invoice.notes || '',
        terms: invoice.terms || '',
        supply_address: invoice.supply_address || '',
        supply_city: invoice.supply_city || '',
        supply_state: invoice.supply_state || '',
        supply_pincode: invoice.supply_pincode || ''
      });

      if (invoice.customer_id) {
        const customerData = {
          id: invoice.customer_id,
          name: invoice.customer_name,
          company_name: invoice.company_name || '',
          email: invoice.customer_email,
          phone: invoice.customer_phone,
          gst_number: invoice.customer_gstin,
          address: invoice.customer_address,
          city: invoice.customer_city || '',
          state: invoice.customer_state || '',
          pincode: invoice.customer_pincode || ''
        };
        setSelectedCustomer(customerData);
        setCustomerSearch(customerData.name + (customerData.company_name ? ` (${customerData.company_name})` : ''));

        const stateForGst = invoice.supply_state || invoice.customer_state || '';
        setGstType(determineGstType(stateForGst));

        if (invoice.customer_gstin) {
          setGstLookupStatus('found');
        }
      }

      if (invoice.items && invoice.items.length > 0) {
        const mappedItems = invoice.items.map(item => ({
          product_id: item.product_id || '',
          item_name: item.item_name || item.product_name || '',
          description: item.description || '',
          hsn_sac: item.hsn_sac || '',
          quantity: parseFloat(item.quantity),
          unit: item.unit || item.product_unit || 'PCS',
          location: item.location || 'Office',
          rate: parseFloat(item.rate),
          tax_rate: parseFloat(item.tax_rate || 0),
          discount: parseFloat(item.discount || 0),
          tax_amount: parseFloat(item.tax_amount || 0),
          amount: parseFloat(item.amount)
        }));
        setItems(mappedItems);

        // Detect if tax was disabled using saved tax_check_box value
        const wasTaxDisabled = invoice.items.some(item => item.tax_check_box === 'yes');
        if (wasTaxDisabled) {
          setTaxDisabled(true);
        }

        // Set product search text for items with selected products
        const searches = {};
        mappedItems.forEach((item, idx) => {
          if (item.product_id) {
            const product = products.find(p => p.id === parseInt(item.product_id));
            searches[idx] = product ? `${product.name} (${product.item_code})` : item.item_name;
          }
        });
        setProductSearches(searches);
      }
    } catch (error) {
      toast.error('Failed to fetch invoice');
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;

    if (field === 'product_id' && value) {
      const product = products.find(p => p.id === parseInt(value));
      if (product) {
        newItems[index].item_name = product.name;
        newItems[index].description = product.description || '';
        newItems[index].unit = product.unit;
        newItems[index].rate = parseFloat(product.price);
        newItems[index].tax_rate = parseFloat(product.tax_rate);
      }
    }

    if (['quantity', 'rate', 'tax_rate', 'discount'].includes(field)) {
      const item = newItems[index];
      const qty = parseFloat(item.quantity) || 0;
      const rate = parseFloat(item.rate) || 0;
      const taxRate = taxDisabled ? 0 : (parseFloat(item.tax_rate) || 0);
      const discount = parseFloat(item.discount) || 0;

      const baseAmount = qty * rate;
      const discountAmount = (baseAmount * discount) / 100;
      const taxableAmount = baseAmount - discountAmount;
      const taxAmount = (taxableAmount * taxRate) / 100;

      newItems[index].tax_amount = taxAmount;
      newItems[index].amount = taxableAmount + taxAmount;
    }

    setItems(newItems);
  };

  // Handle tax disable toggle
  const handleTaxDisableToggle = (checked) => {
    setTaxDisabled(checked);
    const newItems = items.map(item => {
      const qty = parseFloat(item.quantity) || 0;
      const rate = parseFloat(item.rate) || 0;
      const discount = parseFloat(item.discount) || 0;
      const taxRate = checked ? 0 : (parseFloat(item.tax_rate) || 0);

      const baseAmount = qty * rate;
      const discountAmount = (baseAmount * discount) / 100;
      const taxableAmount = baseAmount - discountAmount;
      const taxAmount = (taxableAmount * taxRate) / 100;

      return {
        ...item,
        tax_amount: taxAmount,
        amount: taxableAmount + taxAmount
      };
    });
    setItems(newItems);
  };

  // Get filtered products for a specific item row
  const getFilteredProducts = (index) => {
    const search = (productSearches[index] || '').toLowerCase();
    if (!search) return products;
    return products.filter(p =>
      p.name.toLowerCase().includes(search) ||
      (p.item_code && p.item_code.toLowerCase().includes(search))
    );
  };

  // Handle product selection from searchable dropdown
  const handleProductSelect = (index, product) => {
    handleItemChange(index, 'product_id', product.id.toString());
    setProductSearches(prev => ({ ...prev, [index]: `${product.name} (${product.item_code})` }));
    setShowProductDropdown(prev => ({ ...prev, [index]: false }));
  };

  // Clear product selection for a row
  const handleClearProduct = (index) => {
    handleItemChange(index, 'product_id', '');
    setProductSearches(prev => ({ ...prev, [index]: '' }));
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        product_id: '',
        item_name: '',
        description: '',
        hsn_sac: '',
        quantity: 1,
        unit: 'PCS',
        location: 'Office',
        rate: 0,
        tax_rate: taxDisabled ? 0 : 18,
        discount: 0,
        tax_amount: 0,
        amount: 0
      }
    ]);
  };

  const removeItem = (index) => {
    if (items.length === 1) {
      toast.error('At least one item is required');
      return;
    }
    setItems(items.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const rate = parseFloat(item.rate) || 0;
      return sum + (qty * rate);
    }, 0);

    const total_discount = items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const rate = parseFloat(item.rate) || 0;
      const discount = parseFloat(item.discount) || 0;
      return sum + ((qty * rate * discount) / 100);
    }, 0);

    const total_tax = items.reduce((sum, item) => {
      return sum + (parseFloat(item.tax_amount) || 0);
    }, 0);

    const grand_total = items.reduce((sum, item) => {
      return sum + (parseFloat(item.amount) || 0);
    }, 0);

    const paid_amount = parseFloat(formData.paid_amount) || 0;
    const due_amount = grand_total - paid_amount;

    setTotals({
      subtotal,
      total_discount,
      total_tax,
      grand_total,
      due_amount
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation - Customer must be selected
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }

    if (items.some(item => !item.item_name.trim())) {
      toast.error('All items must have a name');
      return;
    }

    if (items.some(item => parseFloat(item.quantity) <= 0)) {
      toast.error('All items must have quantity greater than 0');
      return;
    }

    try {
      setLoading(true);

      const paidAmount = Number(formData.paid_amount) || 0;

      let payment_status;
      if (isEditMode) {
        payment_status = formData.payment_status;
      } else {
        payment_status = 'unpaid';
        if (paidAmount > 0 && paidAmount < totals.grand_total) {
          payment_status = 'partial';
        } else if (paidAmount >= totals.grand_total) {
          payment_status = 'paid';
        }
      }

      const invoiceData = {
        invoice_type: formData.invoice_type,
        invoice_date: new Date(formData.invoice_date).toISOString(),
        customer_id: selectedCustomer.id,
        customer_name: selectedCustomer.name,

        items: items.map(item => ({
          product_id: item.product_id ? Number(item.product_id) : null,
          name: item.item_name,
          description: item.description || null,
          hsn_sac: item.hsn_sac || null,
          unit: item.unit,
          location: item.location || 'Office',
          quantity: Number(item.quantity),
          rate: Number(item.rate),
          discount: Number(item.discount || 0),
          tax_rate: taxDisabled ? 0 : Number(item.tax_rate || 0),
          tax_amount: taxDisabled ? 0 : Number(item.tax_amount || 0),
          total: Number(item.amount),
          tax_check_box: taxDisabled ? 'yes' : 'no'
        })),

        subtotal: Number(totals.subtotal),
        total_discount: Number(totals.total_discount),
        total_tax: Number(totals.total_tax),
        grand_total: Number(totals.grand_total),
        gst_type: gstType,
        paid_amount: paidAmount,
        payment_status,

        payment_mode: formData.payment_mode || null,
        delivery_status: formData.delivery_status || 'pending',

        notes: formData.notes || null,
        terms_conditions: formData.terms || null,

        supply_address: formData.supply_address || null,
        supply_city: formData.supply_city || null,
        supply_state: formData.supply_state || null,
        supply_pincode: formData.supply_pincode || null
      };

      if (isEditMode) {
        await invoicesAPI.update(id, invoiceData);
        toast.success('Invoice updated successfully');
      } else {
        await invoicesAPI.create(invoiceData);
        toast.success('Invoice created successfully');
      }

      navigate('/invoices');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save invoice');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <div className="p-6 flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
            {isEditMode ? 'Edit Invoice' : 'Create Invoice'}
          </h1>
          <p className="text-gray-600 text-xs sm:text-sm mt-1">
            {isEditMode ? 'Update invoice details' : 'Fill in the details to create a new invoice'}
          </p>
        </div>
        <button
          onClick={() => navigate('/invoices')}
          className="text-gray-600 hover:text-gray-800"
        >
          <FiX size={24} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        {/* Invoice Details */}
        <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Invoice Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Invoice Type <span className="text-red-500">*</span>
              </label>
              <select
                name="invoice_type"
                value={formData.invoice_type}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="tax_invoice">Tax Invoice</option>
                <option value="proforma">Proforma Invoice</option>
                <option value="delivery_challan">Delivery Challan</option>
                <option value="bill_book">Bill Book</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Invoice Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="invoice_date"
                value={formData.invoice_date}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Place of Supply subsection */}
          <div className="mt-6">
            <h3 className="text-md font-medium text-gray-700 mb-3 border-b pb-2">Place of Supply</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <input
                  type="text"
                  name="supply_address"
                  value={formData.supply_address}
                  onChange={handleInputChange}
                  placeholder="Enter supply address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  name="supply_city"
                  value={formData.supply_city}
                  onChange={handleInputChange}
                  placeholder="Enter city"
                  maxLength="100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <input
                  type="text"
                  name="supply_state"
                  value={formData.supply_state}
                  onChange={handleInputChange}
                  placeholder="Enter state"
                  maxLength="100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pincode
                </label>
                <input
                  type="text"
                  name="supply_pincode"
                  value={formData.supply_pincode}
                  onChange={handleInputChange}
                  placeholder="Enter pincode"
                  maxLength="6"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Customer Details */}
        <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Customer Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* GST Number with auto-lookup */}
            {/* <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GST Number
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="customer_gstin"
                  value={formData.customer_gstin}
                  onChange={handleGstChange}
                  onPaste={handleGstPaste}
                  placeholder="Enter GST number (auto-fills customer if found)"
                  maxLength="15"
                  className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    gstLookupStatus === 'invalid' ? 'border-red-300' :
                    gstLookupStatus === 'found' ? 'border-green-300 bg-green-50' :
                    'border-gray-300'
                  }`}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {getGstStatusIndicator()}
                </div>
              </div>
              {gstLookupStatus === 'invalid' && (
                <p className="text-xs text-red-500 mt-1">Invalid GST format</p>
              )}
              {gstLookupStatus === 'found' && (
                <p className="text-xs text-green-600 mt-1">Customer found and selected!</p>
              )}
              {gstLookupStatus === 'not_found' && formData.customer_gstin.length >= 15 && (
                <p className="text-xs text-orange-600 mt-1">New GST - Please select or add a customer</p>
              )}
            </div> */}

            {/* Customer Selection Dropdown */}
            <div className="md:col-span-2" ref={customerDropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Customer <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      setShowCustomerDropdown(true);
                      if (selectedCustomer && e.target.value !== (selectedCustomer.name + (selectedCustomer.company_name ? ` (${selectedCustomer.company_name})` : ''))) {
                        setSelectedCustomer(null);
                        setGstLookupStatus('idle');
                      }
                    }}
                    onFocus={() => setShowCustomerDropdown(true)}
                    placeholder="Search customer by name, phone, or GSTIN..."
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <FiChevronDown
                    className={`absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-transform ${showCustomerDropdown ? 'rotate-180' : ''}`}
                    size={18}
                  />
                </div>

                {/* Dropdown List */}
                {showCustomerDropdown && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {/* Add New Customer Option */}
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddCustomerModal(true);
                        setShowCustomerDropdown(false);
                      }}
                      className="w-full px-4 py-3 text-left flex items-center gap-3 bg-blue-50 hover:bg-blue-100 text-blue-700 border-b border-gray-200 transition-colors"
                    >
                      <FiUserPlus size={18} />
                      <span className="font-medium">Add New Customer</span>
                    </button>

                    {customersLoading ? (
                      <div className="px-4 py-3 text-gray-500 text-center">
                        Loading customers...
                      </div>
                    ) : filteredCustomers.length === 0 ? (
                      <div className="px-4 py-3 text-gray-500 text-center">
                        No customers found
                      </div>
                    ) : (
                      filteredCustomers.slice(0, 50).map(customer => (
                        <button
                          key={customer.id}
                          type="button"
                          onClick={() => handleCustomerSelect(customer)}
                          className={`w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0 transition-colors ${selectedCustomer?.id === customer.id ? 'bg-blue-50' : ''
                            }`}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-medium text-gray-900">{customer.name}</div>
                              {customer.company_name && (
                                <div className="text-sm text-gray-500">{customer.company_name}</div>
                              )}
                            </div>
                            {customer.gst_number && (
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                {customer.gst_number}
                              </span>
                            )}
                          </div>
                          {customer.phone && (
                            <div className="text-sm text-gray-500 mt-1">{customer.phone}</div>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Selected Customer Details Display */}
            {selectedCustomer && (
              <div className="md:col-span-2 mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-green-800 text-lg">{selectedCustomer.name}</h3>
                    {selectedCustomer.company_name && (
                      <p className="text-green-700">{selectedCustomer.company_name}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handleEditCustomer}
                      className="text-orange-600 hover:text-orange-800 text-sm flex items-center gap-1"
                      title="Edit customer details"
                    >
                      <FiEdit2 size={14} />
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCustomer(null);
                        setCustomerSearch('');
                        setGstType('IGST');
                        setGstLookupStatus('idle');
                        setFormData(prev => ({
                          ...prev,
                          customer_name: '',
                          customer_email: '',
                          customer_phone: '',
                          customer_gstin: '',
                          customer_address: ''
                        }));
                      }}
                      className="text-green-600 hover:text-green-800 text-sm underline"
                    >
                      Change
                    </button>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-green-700">
                  {selectedCustomer.phone && (
                    <p><span className="font-medium">Phone:</span> {selectedCustomer.phone}</p>
                  )}
                  {selectedCustomer.email && (
                    <p><span className="font-medium">Email:</span> {selectedCustomer.email}</p>
                  )}
                  {selectedCustomer.gst_number && (
                    <p><span className="font-medium">GSTIN:</span> {selectedCustomer.gst_number}</p>
                  )}
                  {(selectedCustomer.address || selectedCustomer.city || selectedCustomer.state) && (
                    <p className="md:col-span-2">
                      <span className="font-medium">Address:</span>{' '}
                      {[selectedCustomer.address, selectedCustomer.city, selectedCustomer.state, selectedCustomer.pincode]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Invoice Items */}
        <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6">
          <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
              <h2 className="text-lg font-semibold text-gray-800">Invoice Items</h2>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={taxDisabled}
                  onChange={(e) => handleTaxDisableToggle(e.target.checked)}
                  className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                />
                <span className="text-sm text-gray-600">No Tax (Generate bill without tax)</span>
              </label>
            </div>
            <button
              type="button"
              onClick={addItem}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <FiPlus size={16} />
              Add Item
            </button>
          </div>

          <div className="overflow-x-auto" style={{height:"50vh", overflowY:"auto"}}>
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">HSN/SAC</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32">Qty</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">Unit</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-28">Location</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Rate</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">Tax%</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">Disc%</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-28">Amount</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-16">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" style={{zIndex:"100"}}>
                {items.map((item, index) => (
                  <tr key={index}>
                    <td className="px-3 py-2" style={{ minWidth: '220px' }}>
                      <div className="relative" ref={el => productDropdownRefs.current[index] = el}>
                        {item.product_id ? (
                          <div className="flex items-center gap-1">
                            <div className="flex-1 px-2 py-1 border border-green-300 bg-green-50 rounded text-sm truncate">
                              {products.find(p => p.id === parseInt(item.product_id))?.name || item.item_name}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleClearProduct(index)}
                              className="text-gray-400 hover:text-red-500 flex-shrink-0"
                              title="Change product"
                            >
                              <FiX size={14} />
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="relative">
                              <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                              <input
                                type="text"
                                value={productSearches[index] || ''}
                                onChange={(e) => {
                                  setProductSearches(prev => ({ ...prev, [index]: e.target.value }));
                                  setShowProductDropdown(prev => ({ ...prev, [index]: true }));
                                }}
                                onFocus={() => setShowProductDropdown(prev => ({ ...prev, [index]: true }))}
                                placeholder="Search product..."
                                className="w-full pl-7 pr-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            </div>
                            {showProductDropdown[index] && (
                              <div className="absolute z-30 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setShowProductDropdown(prev => ({ ...prev, [index]: false }));
                                  }}
                                  className="w-full px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-50 border-b"
                                >
                                  Custom Item
                                </button>
                                {getFilteredProducts(index).length === 0 ? (
                                  <div className="px-3 py-2 text-sm text-gray-500 text-center">No products found</div>
                                ) : (
                                  getFilteredProducts(index).slice(0, 30).map(product => (
                                    <button
                                      key={product.id}
                                      type="button"
                                      onClick={() => handleProductSelect(index, product)}
                                      className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 border-b border-gray-100 last:border-0"
                                    >
                                      <div className="font-medium text-gray-900">{product.name}</div>
                                      <div className="text-xs text-gray-500">{product.item_code} | ₹{product.price}</div>
                                    </button>
                                  ))
                                )}
                              </div>
                            )}
                          </>
                        )}
                        {!item.product_id && (
                          <input
                            type="text"
                            value={item.item_name}
                            onChange={(e) => handleItemChange(index, 'item_name', e.target.value)}
                            placeholder="Item name"
                            required
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm mt-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2" style={{minWidth:'150px'}}>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        placeholder="Description"
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </td>
                    <td className="px-3 py-2" style={{minWidth:'110px'}}>
                      <input
                        type="text"
                        value={item.hsn_sac}
                        onChange={(e) => handleItemChange(index, 'hsn_sac', e.target.value)}
                        placeholder=""
                        maxLength="20"
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </td>
                    <td className="px-3 py-2" style={{minWidth:'120px'}}>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                        min="0.01"
                        step="0.01"
                        required
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </td>
                    <td className="px-3 py-2" style={{minWidth:'90px'}}>
                      <input
                        type="text"
                        value={item.unit}
                        onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                        placeholder="PCS"
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </td>
                    <td className="px-3 py-2" style={{minWidth:'110px'}}>
                      <select
                        value={item.location || 'Office'}
                        onChange={(e) => handleItemChange(index, 'location', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                      >
                        <option value="Office">Office</option>
                        <option value="Godown">Godown</option>
                        <option value="Factory">Factory</option>
                      </select>
                    </td>
                    <td className="px-3 py-2" style={{minWidth:'120px'}}>
                      <input
                        type="number"
                        value={item.rate}
                        onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                        min="0"
                        step="0.01"
                        required
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </td>
                    <td className="px-3 py-2" style={{minWidth:'100px'}}>
                      <input
                        type="number"
                        value={taxDisabled ? 0 : item.tax_rate}
                        onChange={(e) => handleItemChange(index, 'tax_rate', e.target.value)}
                        min="0"
                        step="0.01"
                        disabled={taxDisabled}
                        className={`w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${taxDisabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
                      />
                    </td>
                    <td className="px-3 py-2" style={{minWidth:'100px'}}>
                      <input
                        type="number"
                        value={item.discount}
                        onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                        min="0"
                        max="100"
                        step="0.01"
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-sm font-medium text-gray-900">
                        ₹{item.amount.toFixed(2)}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="text-red-600 hover:text-red-800"
                        title="Remove item"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="mt-6 flex justify-end">
            <div className="w-full md:w-1/3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">₹{totals.subtotal.toFixed(2)}</span>
              </div>
              {totals.total_discount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Discount:</span>
                  <span className="font-medium text-red-600">-₹{totals.total_discount.toFixed(2)}</span>
                </div>
              )}
              {/* GST Type Display */}
              {taxDisabled ? (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tax:</span>
                  <span className="font-medium text-gray-400">Disabled</span>
                </div>
              ) : gstType === 'CGST_SGST' ? (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">CGST (9%):</span>
                    <span className="font-medium">₹{(totals.total_tax / 2).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">SGST (9%):</span>
                    <span className="font-medium">₹{(totals.total_tax / 2).toFixed(2)}</span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">IGST (18%):</span>
                  <span className="font-medium">₹{totals.total_tax.toFixed(2)}</span>
                </div>
              )}
              {/* GST Type Badge */}
              {selectedCustomer && !taxDisabled && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">GST Type:</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    gstType === 'CGST_SGST'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {gstType === 'CGST_SGST' ? 'Intra-State (CGST+SGST)' : 'Inter-State (IGST)'}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span>Grand Total:</span>
                <span className="text-blue-600">₹{totals.grand_total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Details */}
        <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Payment Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Paid Amount
              </label>
              <input
                type="number"
                name="paid_amount"
                value={formData.paid_amount}
                onChange={handleInputChange}
                min="0"
                step="any"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Mode
              </label>
              <select
                name="payment_mode"
                value={formData.payment_mode}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select mode</option>
                <option value="cash">Cash</option>
                <option value="online">Online Transfer</option>
                <option value="upi">UPI</option>
                <option value="cheque">Cheque</option>
                <option value="card">Card</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Status
              </label>
              <select
                name="payment_status"
                value={formData.payment_status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="unpaid">Unpaid</option>
                <option value="partial">Partial</option>
                <option value="paid">Paid</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Delivery Status
              </label>
              <select
                name="delivery_status"
                value={formData.delivery_status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="pending">Pending</option>
                <option value="delivered">Delivered</option>
                <option value="returnable_basis">Returnable Basis</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Amount
              </label>
              <input
                type="text"
                value={`₹${totals.due_amount.toFixed(2)}`}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 font-medium"
              />
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Additional Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows="4"
                placeholder="Any additional notes..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Terms & Conditions
              </label>
              <textarea
                name="terms"
                value={formData.terms}
                onChange={handleInputChange}
                rows="4"
                placeholder="Terms and conditions..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/invoices')}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <FiSave size={18} />
                {isEditMode ? 'Update Invoice' : 'Create Invoice'}
              </>
            )}
          </button>
        </div>
      </form>

      {/* Add Customer Modal */}
      <AddCustomerModal
        isOpen={showAddCustomerModal}
        onClose={() => setShowAddCustomerModal(false)}
        onCustomerSaved={handleCustomerSaved}
      />

      {/* Edit Customer Modal */}
      <AddCustomerModal
        isOpen={showEditCustomerModal}
        onClose={() => {
          setShowEditCustomerModal(false);
          setCustomerToEdit(null);
        }}
        onCustomerSaved={handleCustomerUpdated}
        customer={customerToEdit}
      />
    </div>
  );
};

export default InvoiceForm;
